// public-routes/auth/Login.jsx - SIMPLE DIRECT SOLUTION
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Alert,
    Spinner,
    InputGroup
} from 'reactstrap';
import {
    FiMail,
    FiLock,
    FiEye,
    FiEyeOff,
    FiLogIn
} from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import { AiOutlineApple } from 'react-icons/ai';
import { supabase } from '../../lib/supabase';
import './login.scss';

const Login = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [checkingSession, setCheckingSession] = useState(true);

    // Function to get user role from user object
    const getUserRole = (user) => {
        if (!user) return 'customer';

        // Check user_metadata first
        if (user.user_metadata?.role) {
            return user.user_metadata.role.toLowerCase();
        }

        // Check for admin email patterns
        const adminEmails = ['admin@', 'administrator@', 'superadmin@'];
        if (adminEmails.some(pattern => user.email?.includes(pattern))) {
            return 'admin';
        }

        return 'customer';
    };

    // Function to redirect user based on role
    const redirectUser = (user) => {
        const userRole = getUserRole(user);
        console.log('User role for redirect:', userRole);

        const from = location.state?.from?.pathname;

        if (from) {
            console.log('Redirecting to from location:', from);
            navigate(from, { replace: true });
            return;
        }

        // Role-based redirect
        if (userRole === 'admin' || userRole === 'administrator' || userRole === 'superadmin') {
            console.log('Redirecting admin to /admin/dashboard');
            navigate('/admin/dashboard', { replace: true });
        } else {
            console.log('Redirecting customer to /panel/dashboard');
            navigate('/panel/dashboard', { replace: true });
        }
    };

    // Check if user is already logged in
    useEffect(() => {
        const checkSession = async () => {
            try {
                console.log('Checking for existing session...');

                // Get current session
                const { data: { session }, error } = await supabase.auth.getSession();

                if (error) {
                    console.error('Session check error:', error);
                    setCheckingSession(false);
                    return;
                }

                // Check if session exists and is not expired
                if (session) {
                    const currentTime = Math.floor(Date.now() / 1000);
                    const sessionValid = session.expires_at && currentTime < session.expires_at;

                    console.log('Session found, valid:', sessionValid, 'Expires at:', session.expires_at, 'Current time:', currentTime);

                    if (sessionValid) {
                        // Get user details
                        const { data: { user } } = await supabase.auth.getUser();

                        if (user) {
                            console.log('User already authenticated:', user.email);
                            redirectUser(user);
                            return;
                        }
                    }
                }

                console.log('No valid session found');
                setCheckingSession(false);
            } catch (error) {
                console.error('Error checking session:', error);
                setCheckingSession(false);
            }
        };

        checkSession();

        // Set up auth state listener
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, session) => {
                console.log('Auth state changed:', event);

                if (event === 'SIGNED_IN' && session?.user) {
                    console.log('User signed in via OAuth');
                    redirectUser(session.user);
                }
            }
        );

        return () => {
            subscription.unsubscribe();
        };
    }, [navigate, location]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormLoading(true);

        // Validate form
        if (!email || !password) {
            setFormError('Please enter both email and password');
            setFormLoading(false);
            return;
        }

        if (!validateEmail(email)) {
            setFormError('Please enter a valid email address');
            setFormLoading(false);
            return;
        }

        try {
            console.log('Attempting login for:', email);

            // Sign in with Supabase
            const { data, error } = await supabase.auth.signInWithPassword({
                email: email.trim(),
                password: password,
            });

            if (error) {
                throw error;
            }

            if (data?.user && data?.session) {
                console.log('Login successful for:', data.user.email);

                // Handle remember me option
                if (rememberMe) {
                    await supabase.auth.setSession({
                        access_token: data.session.access_token,
                        refresh_token: data.session.refresh_token,
                    });
                }

                // Redirect based on role
                redirectUser(data.user);

            } else {
                throw new Error('Login failed. Please try again.');
            }
        } catch (error) {
            console.error('Login error:', error);

            // Map Supabase errors to user-friendly messages
            let errorMessage = 'Login failed. Please try again.';

            if (error.message.includes('Invalid login credentials')) {
                errorMessage = 'Invalid email or password. Please try again.';
            } else if (error.message.includes('Email not confirmed')) {
                errorMessage = 'Please verify your email address before logging in.';
            } else if (error.message.includes('Invalid email')) {
                errorMessage = 'Please enter a valid email address.';
            } else if (error.message.includes('rate limit')) {
                errorMessage = 'Too many attempts. Please try again in a few minutes.';
            }

            setFormError(errorMessage);
            setFormLoading(false);
        }
    };

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    const handleForgotPassword = () => {
        navigate('/forgot-password');
    };

    const handleGoogleLogin = async () => {
        try {
            setFormLoading(true);
            setFormError('');

            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    }
                },
            });

            if (error) throw error;
        } catch (error) {
            console.error('Google login error:', error);
            setFormError('Google login failed. Please try again.');
            setFormLoading(false);
        }
    };

    const handleAppleLogin = async () => {
        try {
            setFormLoading(true);
            setFormError('');

            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'apple',
                options: {
                    redirectTo: `${window.location.origin}/auth/callback`,
                },
            });

            if (error) throw error;
        } catch (error) {
            console.error('Apple login error:', error);
            setFormError('Apple login failed. Please try again.');
            setFormLoading(false);
        }
    };

    const handleRegister = () => {
        navigate('/register');
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    // Show loading spinner while checking session
    if (checkingSession) {
        return (
            <div className="loading-spinner-custom">
                <div className="text-center">
                    <Spinner color="light" size="lg" />
                    <p className="spinner-text-custom">Checking authentication...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page-custom">
            <Container>
                <Row className="justify-content-center">
                    <Col md={8} lg={6} xl={5}>
                        <div className="brand-logo-custom mb-5">
                            <div className="logo-icon-custom">
                                <FiLogIn size={32} color="white" />
                            </div>
                            <h2>Welcome Back</h2>
                            <p className="text-muted">Sign in to your account to continue</p>
                        </div>

                        <Card className="login-card-custom">
                            <CardBody className="p-5">
                                {formError && (
                                    <Alert
                                        color="danger"
                                        className="alert-custom alert-custom-danger"
                                    >
                                        <div className="d-flex align-items-center">
                                            <i className="fas fa-exclamation-circle me-2"></i>
                                            <span>{formError}</span>
                                        </div>
                                    </Alert>
                                )}

                                {/* Social Login Buttons */}
                                <div className="text-center social-login-custom">
                                    <Button
                                        color="outline"
                                        className="mx-auto btn-custom-outline w-100 mb-3 d-flex align-items-center justify-content-center"
                                        onClick={handleGoogleLogin}
                                        disabled={formLoading}
                                    >
                                        <FcGoogle size={20} className="me-3" />
                                        Continue with Google
                                    </Button>

                                    <Button
                                        color="outline"
                                        className="mx-auto btn-custom-outline w-100 mb-4 d-flex align-items-center justify-content-center"
                                        onClick={handleAppleLogin}
                                        disabled={formLoading}
                                    >
                                        <AiOutlineApple size={20} className="me-3" />
                                        Continue with Apple
                                    </Button>

                                    <div className="divider-custom">
                                        <span>or</span>
                                    </div>
                                </div>

                                <Form onSubmit={handleLogin}>
                                    <FormGroup className="mb-4">
                                        <Label for="email" className="d-flex mb-2 form-label fw-medium">
                                            <FiMail className="me-2" />
                                            Email Address
                                        </Label>
                                        <InputGroup>
                                            <Input
                                                id="email"
                                                type="email"
                                                placeholder="Enter your email address"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                disabled={formLoading}
                                                className="input-custom input-custom-large"
                                                autoComplete="email"
                                                required
                                            />
                                        </InputGroup>
                                    </FormGroup>

                                    <FormGroup className="mb-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <Label for="password" className="d-flex form-label fw-medium">
                                                <FiLock className="me-2" />
                                                Password
                                            </Label>
                                            <Button
                                                color="link"
                                                size="sm"
                                                className="link-custom p-0"
                                                onClick={handleForgotPassword}
                                                disabled={formLoading}
                                            >
                                                Forgot Password?
                                            </Button>
                                        </div>
                                        <InputGroup>
                                            <Input
                                                id="password"
                                                type={showPassword ? 'text' : 'password'}
                                                placeholder="Enter your password"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                disabled={formLoading}
                                                className="input-custom input-custom-large"
                                                autoComplete="current-password"
                                                required
                                            />
                                            <Button
                                                color="link"
                                                className="password-toggle-btn"
                                                onClick={togglePasswordVisibility}
                                                disabled={formLoading}
                                                type="button"
                                            >
                                                {showPassword ? <FiEyeOff /> : <FiEye />}
                                            </Button>
                                        </InputGroup>
                                    </FormGroup>

                                    <FormGroup className="mb-4">
                                        <div className="form-check">
                                            <Input
                                                type="checkbox"
                                                id="rememberMe"
                                                checked={rememberMe}
                                                onChange={(e) => setRememberMe(e.target.checked)}
                                                disabled={formLoading}
                                                className="form-check-input"
                                            />
                                            <Label
                                                htmlFor="rememberMe"
                                                className="form-check-label text-muted"
                                                style={{ cursor: 'pointer' }}
                                            >
                                                Remember me for 30 days
                                            </Label>
                                        </div>
                                    </FormGroup>

                                    <Button
                                        type="submit"
                                        color="primary"
                                        className="btn-custom-primary w-100 mb-4"
                                        disabled={formLoading || !email || !password}
                                    >
                                        {formLoading ? (
                                            <>
                                                <Spinner size="sm" className="me-2" />
                                                Signing In...
                                            </>
                                        ) : (
                                            <>
                                                <FiLogIn className="me-2" />
                                                Sign In
                                            </>
                                        )}
                                    </Button>

                                    <div className="text-center">
                                        <p className="text-muted mb-0">
                                            Don't have an account?{' '}
                                            <Button
                                                color="link"
                                                className="link-custom fw-medium p-0"
                                                onClick={handleRegister}
                                                disabled={formLoading}
                                            >
                                                Sign up
                                            </Button>
                                        </p>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>

                        <div className="text-center mt-4">
                            <p className="text-muted small">
                                By signing in, you agree to our{' '}
                                <Link to="/terms" className="link-custom">Terms</Link>{' '}
                                and{' '}
                                <Link to="/privacy" className="link-custom">Privacy Policy</Link>
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Login;